/**
 * Auth Context
 *
 * 使用Line ID 進行登入
 * 在此判斷會員是否已註冊過
 * 若是已註冊，API會回傳 access Token
 * 若尚未註冊，則會吐回404
 */
import { Login } from 'api';
import { getCouponWithoutToken } from 'api/coupon';
import type { MeData } from 'api/me';
import { getMe } from 'api/me';
import { useBrand } from 'context/Brand';
import { useLiff } from 'context/Liff';
import type { Consumer, Context, FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface AuthProviderProps {
  children?: ReactNode;
  lineToken: string;
  brandID: number;
}
interface UserData extends Partial<MeData> {
  isLoggedIn?: boolean;
  isMember?: boolean;
  isNormal?: boolean;
  isSuspended?: boolean;
  accessToken?: string | null;
}
interface AuthContext {
  error?: any;
  logging: boolean;
  user?: UserData;
  auth: (params?: { autoRegister?: boolean }) => void;
  fetching: boolean;
  queryMe?: () => Promise<void>;
}
type CreateAuthContext = () => {
  AuthConsumer: Consumer<AuthContext>;
  AuthProvider: FC<AuthProviderProps>;
  useAuth: () => AuthContext;
};

const isSuspensionBlockedError = (error: any) => {
  const errorCode =
    error?.response?.data?.error_code || error?.response?.data?.data?.error_code;

  return errorCode === 'BLK002';
};

const isSuspensionFeatureBlockedError = (error: any) => {
  const errorCode =
    error?.response?.data?.error_code || error?.response?.data?.data?.error_code;

  return errorCode === 'BLK008';
};

const createAuthProvider = (context: Context<AuthContext>) => {
  const AuthProvider: FC<AuthProviderProps> = ({
    children,
    lineToken,
    brandID,
  }) => {
    const [authState, setAuthState] = useState<{
      error?: unknown;
      logging: boolean;
      user?: UserData;
      fetching: boolean;
    }>({
      logging: true,
      fetching: false,
    });
    const { liff } = useLiff();
    const { brandInfo } = useBrand();
    const collectID = useMemo(() => {
      const searchParams = new URLSearchParams(window.location.search);
      return searchParams.get('collect');
    }, []);
    const shouldAutoRegisterSurvey = useMemo(() => {
      const searchParams = new URLSearchParams(window.location.search);
      const pathname = window.location.pathname;

      return pathname.includes('/survey') && !!searchParams.get('svy')?.trim();
    }, []);

    const getMeData = useCallback(async (token: string) => {
      try {
        const resp = await getMe(token);
        if (resp.error) {
          if (isSuspensionFeatureBlockedError(resp.error)) {
            return { is_suspended: true, member_status_id: 2 } as MeData;
          }
          return undefined;
        }
        return resp.data;
      } catch (error) {
        console.log(error);
        if (isSuspensionFeatureBlockedError(error)) {
          return { is_suspended: true, member_status_id: 2 } as MeData;
        }
        return undefined;
      }
    }, []);

    const auth = useCallback(
      async (params?: { autoRegister?: boolean }) => {
        try {
          const { autoRegister = false } = params ?? {};

          const { error: loginError, data: resp } = await Login(
            lineToken,
            brandID,
            autoRegister,
          );

          if (loginError) {
            if (isSuspensionBlockedError(loginError)) {
              setAuthState((prev) => ({
                ...prev,
                error: undefined,
                user: {
                  accessToken: undefined,
                  isLoggedIn: false,
                  isNormal: false,
                  isMember: false,
                  isSuspended: true,
                },
                logging: false,
              }));
            } else if (loginError.response?.status === 401) {
              setAuthState((prev) => ({
                ...prev,
                error: loginError,
                user: {
                  accessToken: undefined,
                  isLoggedIn: false,
                  isNormal: false,
                  isMember: false,
                  isSuspended: false,
                },
                logging: false,
              }));

              liff.logout();
            } else if (loginError.response?.status === 404) {
              setAuthState((prev) => ({
                ...prev,
                user: {
                  accessToken: undefined,
                  isLoggedIn: false,
                  isNormal: false,
                  isMember: false,
                  isSuspended: false,
                },
                logging: false,
              }));
            } else {
              setAuthState((prev) => ({
                ...prev,
                error: loginError,
                user: {
                  accessToken: undefined,
                  isLoggedIn: false,
                  isNormal: false,
                  isMember: false,
                  isSuspended: false,
                },
                logging: false,
              }));
            }
            return;
          }
          if (resp) {
            const { data } = resp;

            const meData = await getMeData(data.access_token);
            setAuthState((prev) => ({
              ...prev,
              error: undefined,
              user: {
                ...meData,
                accessToken: data.access_token,
                isLoggedIn: true,
                isNormal: data.is_common === 1,
                isMember: data.is_common === 0,
                isSuspended:
                  meData?.is_suspended === true || meData?.member_status_id === 2,
              },
              logging: false,
            }));
          }
        } catch (error) {
          setAuthState((prev) => ({
            ...prev,
            error,
            user: {
              accessToken: undefined,
              isLoggedIn: false,
              isNormal: false,
              isMember: false,
              isSuspended: false,
            },
            logging: false,
          }));
        }
      },
      [brandID, liff, lineToken, getMeData],
    );

    const checkCoupon = useCallback(
      async (id: string) => {
        try {
          if (brandInfo?.id) {
            const { data } = await getCouponWithoutToken(id, {
              brand_id: brandInfo.id,
            });
            return data;
          }
          return undefined;
        } catch (_error) {
          return undefined;
        }
      },
      [brandInfo?.id],
    );

    const fetchMe = useCallback(async (token: string) => {
      try {
        setAuthState((prev) => ({
          ...prev,
          fetching: true,
        }));
        const resp = await getMe(token);
        if (resp.error) {
          setAuthState((prev) => ({
            ...prev,
            error: undefined,
            user: isSuspensionFeatureBlockedError(resp.error)
              ? { ...prev.user, isSuspended: true }
              : prev.user,
            fetching: false,
          }));
          return;
        }
        setAuthState((prev) => ({
          ...prev,
          error: undefined,
          user: {
            ...prev.user,
            ...resp.data,
            isSuspended:
              resp.data?.is_suspended === true ||
              resp.data?.member_status_id === 2,
          },
          fetching: false,
        }));
      } catch (error) {
        console.log(error);
        setAuthState((prev) => ({
          ...prev,
          error: undefined,
          user: isSuspensionFeatureBlockedError(error)
            ? { ...prev.user, isSuspended: true }
            : prev.user,
          fetching: false,
        }));
      }
    }, []);

    const queryMe = useMemo(() => {
      if (authState.user?.accessToken) {
        const token = authState.user?.accessToken;
        return () => fetchMe(token);
      }
      return undefined;
    }, [fetchMe, authState.user?.accessToken]);

    useEffect(() => {
      if (lineToken && brandID) {
        (async () => {
          try {
            if ((collectID !== undefined && collectID !== null) || collectID) {
              const coupon = await checkCoupon(collectID);
              if (coupon && coupon.share_type === 0) {
                await auth({ autoRegister: true });
              } else {
                await auth();
              }
            } else if (shouldAutoRegisterSurvey) {
              await auth({ autoRegister: true });
            } else {
              await auth();
            }
          } catch (error) {
            console.log(error);
            setAuthState((prev) => ({
              ...prev,
              error,
            }));
          }
        })();
      } else {
        console.error('lineToken && brandID', lineToken, brandID);
      }
    }, [
      lineToken,
      brandID,
      collectID,
      shouldAutoRegisterSurvey,
      checkCoupon,
      auth,
    ]);

    return (
      <context.Provider
        value={{
          ...authState,
          auth,
          queryMe,
        }}
      >
        {children}
      </context.Provider>
    );
  };

  return AuthProvider;
};
export const createAuthContext: CreateAuthContext = () => {
  const context = createContext<AuthContext>({
    logging: true,
    auth: () => {},
    fetching: false,
    user: undefined,
  });
  context.displayName = 'AuthContext';

  return {
    AuthConsumer: context.Consumer,
    AuthProvider: createAuthProvider(context),
    useAuth: () => useContext(context),
  };
};
export const { AuthConsumer, AuthProvider, useAuth } = createAuthContext();
